import {useState, useEffect} from 'react';
import {LOCAL_STORAGE_DELIMITER} from "../Config";

export default function useLocalStorage() {
    const [, forceUpdate] = useState({});

    const getItems = (key: string): string[] => {
        try {
            const items = localStorage.getItem(key);
            return items ? items.split(LOCAL_STORAGE_DELIMITER).filter(item => item.trim() !== '') : [];
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return [];
        }
    }

    const setItems = (key: string, items: string[]): void => {
        try {
            localStorage.setItem(key, items.join(LOCAL_STORAGE_DELIMITER));
            forceUpdate({});
        } catch (error) {
            console.error('Error set from localStorage:', error);
        }
    }

    const addItem = (key: string, value: string): void => {
        try {
            const items = getItems(key);
            if (!items.includes(value)) {
                items.push(value);
                const limitedItems = items.slice(-10); // Keep only the last 10 items
                localStorage.setItem(key, limitedItems.join(LOCAL_STORAGE_DELIMITER));
                forceUpdate({});
            }
        } catch (error) {
            console.error('Error writing to localStorage:', error);
        }
    }

    const toggleItem = (key: string, value: string): void => {
        try {
            if (hasItem(key, value)) {
                removeItem(key, value);
            } else {
                addItem(key, value);
            }
            forceUpdate({});
        } catch (error) {
            console.error('Error toggle from localStorage:', error);
        }
    }

    const removeItem = (key: string, value: string): void => {
        try {
            const updatedItems = getItems(key).filter((item: string) => item !== value);
            localStorage.setItem(key, updatedItems.join(LOCAL_STORAGE_DELIMITER));
            forceUpdate({});
        } catch (error) {
            console.error('Error removing from localStorage:', error);
        }
    }

    const hasItem = (key: string, value: string): boolean => {
        return getItems(key).includes(value);
    }

    const getObject = (key: string): any => {
        try {
            const items = localStorage.getItem(key);
            return items ? JSON.parse(items) : [];
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return [];
        }
    }

    const setObject = (key: string, items: any): void => {
        try {
            localStorage.setItem(key, JSON.stringify(items));
            forceUpdate({});
        } catch (error) {
            console.error('Error set from localStorage:', error);
        }
    }

    return {getItems, setItems, addItem, removeItem, hasItem, toggleItem, getObject, setObject};
}